import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Store, Eye, Loader2, Info } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { DataTable2 } from '../components/ui/DataTable2';
import SelectBox from '../components/ui/SelectBox';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchVendors, fetchGoodsReceipts } from '../apiHelper';
import Dropdown from '../components/ui/Dropdown';

const client = new FastAPIClient();

const VendorManagement = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [user, setUser] = useState(null);

    // Modal & Form State
    const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);
    const [loadingVendorCreation, setLoadingVendorCreation] = useState(false);
    const [vendorErrors, setVendorErrors] = useState({});

    // Form Fields
    const [vendorName, setVendorName] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [country, setCountry] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [contactTitle, setContactTitle] = useState('');     // e.g., Mr, Ms, Dr
    const [contactName, setContactName] = useState('');       // Full name (required)

    // Table State
    const [vendors, setVendors] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(true);
    const [errorVendors, setErrorVendors] = useState(null);

    // Load user
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to manage vendors.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Load vendors
    const loadVendors = useCallback(async () => {
        if (!user) return;
        setLoadingVendors(true);
        setErrorVendors(null);
        try {
            const fetchedVendors = await fetchVendors(user);
            // console.log(fetchedVendors)
            setVendors(fetchedVendors.map(vendor => {
                // Find the primary/self address (selfaddress === 1)
                const primaryAddress = vendor.member?.addresses?.find(
                    addr => addr.selfaddress === 1
                ) || vendor.member?.addresses?.[0]; // fallback to first if none

                // Extract contact name parts
                const firstName = primaryAddress?.first_name || '';
                const middleName = primaryAddress?.middle_name || '';
                const lastName = primaryAddress?.last_name || '';
                const contactName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim() || 'N/A';

                // Extract other details
                const addressLine = primaryAddress?.address || 'N/A';
                const city = primaryAddress?.city || '';
                const state = primaryAddress?.state || '';
                const country = primaryAddress?.country || '';
                const fullLocation = [addressLine, city, state, country].filter(Boolean).join(', ') || 'N/A';

                const phone = primaryAddress?.phone1 || 'N/A';

                // Buyer organization name
                const buyerName = `${vendor.buyer?.user?.first_name || ''
                    } ${vendor.buyer?.user?.last_name || ''}`.trim() || 'Unknown Buyer';

                return {
                    id: vendor.vendor_id,
                    name: vendor.vendorname || 'Unnamed Vendor',
                    contactName,
                    address: fullLocation,
                    phone,
                    buyerName,
                    data: vendor // keep full data if needed later
                };
            }));
            // setVendors(fetchedVendors.map(vendor => ({
            //     id: vendor.vendor_id,
            //     name: vendor.vendorname,
            //     buyerName: `${vendor.buyer?.user?.first_name || ''} ${vendor.buyer?.user?.last_name || ''}`.trim(),
            //     data: vendor
            // })));
        } catch (err) {
            console.error("Error loading vendors:", err);
            const errorMessage = err.response?.data?.detail || `Failed to load vendors: ${err.message || err}`;
            setErrorVendors(errorMessage);
            showToast(errorMessage, "error");
        } finally {
            setLoadingVendors(false);
        }
    }, [user, showToast]);

    useEffect(() => {
        loadVendors();
    }, [loadVendors]);

    // Handle vendor creation
    const handleCreateVendor = useCallback(async (e) => {
        e.preventDefault();
        setVendorErrors({});

        // Validation
        const errors = {};
        if (!vendorName.trim()) errors.vendorName = "Vendor name is required.";
        if (!address.trim()) errors.address = "Address is required.";
        if (!phoneNumber.trim()) errors.phoneNumber = "Phone number is required.";
        if (!contactName.trim()) errors.contactName = "Contact person name is required.";

        if (Object.keys(errors).length > 0) {
            setVendorErrors(errors);
            return;
        }

        if (!user || !user.users_id) {
            showToast("User not logged in or user ID not found.", "error");
            return;
        }

        setLoadingVendorCreation(true);

        const payload = {
            buyer_id: user.users_id,
            vendorname: vendorName.trim(),
            address: address.trim(),
            city: city.trim() || null,
            state: state.trim() || null,
            country: country.trim() || null,
            phone_number: phoneNumber.trim(),
            contact_title: contactTitle || null,        // Optional
            contact_name: contactName.trim(),           // Required
            markfordelete: 0,
        };

        try {
            console.log('Sending payload to create vendor:', payload);
            const response = await client.postData('/vendor/create', payload);
            showToast(response.msg || "Vendor created successfully!", "success");

            // Reset form
            setVendorName('');
            setAddress('');
            setCity('');
            setState('');
            setCountry('');
            setPhoneNumber('');
            setContactName('');
            setContactTitle('');

            setShowCreateVendorModal(false);
            loadVendors();
        } catch (err) {
            console.error("Error creating vendor:", err);
            const errorMessage = err.response?.data?.detail || `Failed to create vendor: ${err.message || err}`;
            showToast(errorMessage, "error");
            setVendorErrors({ api: errorMessage });
        } finally {
            setLoadingVendorCreation(false);
        }
    }, [
        vendorName, address, city, state, country, phoneNumber,
        contactName, contactTitle, phoneNumber, user, showToast, loadVendors
    ]);

    // Table columns
    const vendorColumns = useMemo(() => [
        { key: 'name', label: 'Vendor Name' },
        { key: 'contactName', label: 'Contact Person' },
        { key: 'address', label: 'Address' },
        { key: 'phone', label: 'Phone' },
        { key: 'buyerName', label: 'Buyer Organization' },
        {
            key: 'actions',
            label: 'Actions',
            render: (value, row) => (
                <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                        onClick={() => navigate('/vendor/create-purchase-orders', {
                            state: { vendor_id: row.id, vendor_name: row.name }
                        })}
                        variant="secondary"
                        size="sm"
                        className="flex items-center justify-center gap-1 w-full sm:w-auto"
                    >
                        <Plus size={16} /> Create PO
                    </Button>
                    <Button
                        onClick={() => navigate('/vendor/view-purchase-orders', {
                            state: { vendor_id: row.id, vendor_name: row.name }
                        })}
                        variant="secondary"
                        size="sm"
                        className="flex items-center justify-center gap-1 w-full sm:w-auto"
                    >
                        <Eye size={16} /> View POs
                    </Button>
                </div>
            )
        }
    ], [navigate]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to manage vendors.</p>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="mb-6 p-4 sm:p-6 shadow-lg rounded-lg">
                <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center gap-2">
                    <Store size={24} className="text-purple-600" /> Vendor Management
                </h2>
                <p className="text-text-secondary mb-6">
                    Manage your vendors here. Create new vendors or view details and create purchase orders for existing ones.
                </p>

                {/* <div className="flex justify-between items-center mb-6">
                    <Button
                        onClick={() => setShowCreateVendorModal(true)}
                        variant="primary"
                        className="flex items-center gap-2"
                        disabled={!user}
                    >
                        <Plus size={20} /> Create New Vendor
                    </Button>
                </div> */}
                <div className="flex justify-between items-center mb-6">
                    <Dropdown
                        trigger={
                            <Button
                                variant="primary"
                                className="flex items-center gap-2"
                                disabled={!user}
                            >
                                <Plus size={20} /> Create New Vendor
                            </Button>
                        }
                        align="right"
                    >
                        <div className="py-1">
                            {/* Option 1: Create New (single vendor) */}
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateVendorModal(true);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                                <Plus size={16} />
                                Create New
                            </button>

                            {/* Option 2: Import List (bulk) */}
                            <button
                                type="button"
                                onClick={() => {
                                    navigate('/import-data', {
                                        state: {
                                            target: 'batchvendors'
                                        }
                                    })
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 border-t border-gray-200"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                </svg>
                                Import List
                            </button>
                        </div>
                    </Dropdown>
                </div>
                {loadingVendors ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading vendors...</p>
                    </div>
                ) : errorVendors ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{errorVendors}</p>
                    </div>
                ) : (
                    <DataTable2
                        columns={vendorColumns}
                        data={vendors}
                        loading={loadingVendors}
                        error={errorVendors}
                        emptyMessage="No vendors found. Click 'Create New Vendor' to add one."
                        enableSearch={true}
                        enablePagination={true}
                        rowsPerPage={5}
                    />
                )}
            </Card>

            {/* Enhanced Vendor Creation Modal */}
            <Modal
                isOpen={showCreateVendorModal}
                onClose={() => setShowCreateVendorModal(false)}
                title="Create New Vendor"
            >
                <form onSubmit={handleCreateVendor} className="p-6 space-y-6">
                    {vendorErrors.api && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md" role="alert">
                            <p>{vendorErrors.api}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Vendor Info */}
                        <Input
                            label="Vendor Name "
                            id="vendor-name"
                            type="text"
                            placeholder="Best Supplies Inc."
                            value={vendorName}
                            onChange={(e) => setVendorName(e.target.value)}
                            error={vendorErrors.vendorName}
                            required
                        />

                        <Input
                            label="Phone Number"
                            id="phone-number"
                            type="tel"
                            placeholder="(+234) 123-4567"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            error={vendorErrors.phoneNumber}
                            required
                        />

                        <Input
                            label="Address "
                            id="address"
                            type="text"
                            placeholder="123 Business Avenue"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            error={vendorErrors.address}
                            required
                        />

                        <Input
                            label="City"
                            id="city"
                            type="text"
                            placeholder="Ikeja"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />

                        <Input
                            label="State / Province"
                            id="state"
                            type="text"
                            placeholder="Lagos"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                        />

                        <Input
                            label="Country"
                            id="country"
                            type="text"
                            placeholder="Nigeria"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                        />

                        {/* Contact Person - Title + Name */}
                        <SelectBox
                            id="contact-title"
                            name="contact_title"
                            label="Contact Person Title"
                            value={contactTitle}
                            onChange={(e) => setContactTitle(e.target.value)}
                            options={[
                                { value: '', label: '— None —' },
                                { value: 'Mr.', label: 'Mr.' },
                                { value: 'Ms.', label: 'Ms.' },
                                { value: 'Mrs.', label: 'Mrs.' },
                                { value: 'Miss', label: 'Miss' },
                                { value: 'Dr.', label: 'Dr.' },
                                { value: 'Prof.', label: 'Prof.' },
                                { value: 'Eng.', label: 'Eng.' },
                            ]}
                        />

                        <Input
                            label="Contact Person Name "
                            id="contact-name"
                            type="text"
                            placeholder="John Doe"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            error={vendorErrors.contactName}
                            required
                        />
                    </div>

                    <div className="flex justify-end gap-4 mt-8">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setShowCreateVendorModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={loadingVendorCreation}
                        >
                            {loadingVendorCreation ? 'Creating...' : 'Create Vendor'}
                        </Button>
                    </div>
                </form>
            </Modal>

        </div>
    );
};

export default VendorManagement;